import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import {
  findRessourceBySlug,
  getRelatedRessources,
} from "@/data/ressources";

function Block({ block }) {
  if (block.type === "h2") {
    return (
      <h2 className="font-serif font-normal text-[#181A1D] text-2xl lg:text-[28px] leading-snug tracking-tight mt-14 mb-5 text-balance">
        {block.text}
      </h2>
    );
  }
  if (block.type === "frise") {
    return (
      <div
        data-testid="ressource-frise"
        className="my-12 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8"
      >
        {block.title ? (
          <p className="label-eyebrow mb-7">{block.title}</p>
        ) : null}
        <ol>
          {block.items.map((it, i) => (
            <li key={it} className="relative flex items-start gap-5 pb-7 last:pb-0">
              {i < block.items.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute left-[13px] top-8 bottom-0 w-px bg-[#E2DEDA]"
                />
              ) : null}
              <span className="relative flex-shrink-0 w-7 h-7 rounded-full border border-[#D8D3CD] bg-white flex items-center justify-center font-mono text-[10px] text-[#666A70]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm lg:text-[15px] text-[#52565C] font-normal leading-relaxed pt-[5px] text-pretty">
                {it}
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it) => (
          <li
            key={it}
            className="text-base lg:text-[17px] leading-[1.75] text-[#52565C] font-normal flex items-start gap-3"
          >
            <span
              aria-hidden="true"
              className="mt-[12px] w-1 h-1 rounded-full bg-[#8B8F95] flex-shrink-0"
            />
            <span className="text-pretty">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "links") {
    return (
      <aside
        data-testid="ressource-links"
        className="mt-12 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8"
      >
        <p className="label-eyebrow mb-5">{block.title || "Pour aller plus loin"}</p>
        <ul className="space-y-3">
          {block.items.map((it) => (
            <li key={it.to}>
              <Link
                to={it.to}
                className="text-sm lg:text-[15px] text-[#181A1D] font-normal underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors inline-flex items-center gap-2"
              >
                {it.label}
                <ArrowUpRight size={12} strokeWidth={1.5} />
              </Link>
            </li>
          ))}
        </ul>
      </aside>
    );
  }
  if (block.type === "encadre") {
    return (
      <aside
        data-testid="ressource-encadre"
        className="my-12 bg-white border border-[#E2DEDA] rounded-md p-7 lg:p-8"
      >
        <p className="label-eyebrow mb-4">{block.title}</p>
        <p className="text-sm lg:text-[15px] text-[#5A5E64] font-normal leading-relaxed text-pretty mb-5">
          {block.text}
        </p>
        {block.to ? (
          <Link
            to={block.to}
            className="text-sm lg:text-[15px] text-[#181A1D] font-normal underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors inline-flex items-center gap-2"
          >
            {block.label}
            <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        ) : null}
      </aside>
    );
  }
  if (block.type === "note") {
    return (
      <aside
        data-testid="ressource-note"
        className="mt-12 border-l border-[#D8D3CD] pl-5 py-2"
      >
        <p className="label-eyebrow mb-2">Note</p>
        <p className="text-sm lg:text-[15px] text-[#5A5E64] font-normal italic leading-relaxed text-pretty">
          {block.text}
        </p>
      </aside>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="my-10 pl-6 border-l-2 border-[#181A1D]/30 font-serif italic text-lg lg:text-xl text-[#181A1D] leading-relaxed text-pretty">
        « {block.text} »
      </blockquote>
    );
  }
  return (
    <p className="text-base lg:text-[17px] leading-[1.75] text-[#52565C] font-normal text-pretty mb-5">
      {block.text}
    </p>
  );
}

export default function Ressource() {
  const { slug } = useParams();
  const article = findRessourceBySlug(slug);

  if (!article) {
    return <NotFound />;
  }

  const related = getRelatedRessources(article.slug, 3);

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col">
      <Navbar />

      <main className="flex-1">
        <article className="bg-[#FAF9F7]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-16 lg:pb-20">
            <nav
              aria-label="Fil d'Ariane"
              data-testid="ressource-breadcrumb"
              className="mb-12"
            >
              <ol className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs tracking-[0.14em] uppercase text-[#666A70]">
                <li>
                  <Link
                    to="/"
                    data-testid="breadcrumb-accueil"
                    className="hover:text-[#181A1D] transition-colors"
                  >
                    Accueil
                  </Link>
                </li>
                <li aria-hidden="true" className="text-[#8B8F95]">
                  ›
                </li>
                <li>
                  <Link
                    to="/comprendre"
                    data-testid="breadcrumb-comprendre"
                    className="hover:text-[#181A1D] transition-colors"
                  >
                    Comprendre
                  </Link>
                </li>
                <li aria-hidden="true" className="text-[#8B8F95]">
                  ›
                </li>
                <li
                  aria-current="page"
                  data-testid="breadcrumb-current"
                  className="normal-case tracking-normal text-[#8B8F95] font-normal"
                >
                  {article.titre}
                </li>
              </ol>
            </nav>

            <p
              data-testid="ressource-category"
              className="label-eyebrow text-[#666A70] mb-7"
            >
              {article.categorie}
            </p>

            <h1
              data-testid="ressource-title"
              className="font-serif font-normal text-[#181A1D] text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.08] text-balance"
            >
              {article.titre}
            </h1>

            <div aria-hidden="true" data-testid="h1-marker" className="h1-marker mt-8" />

            <p
              data-testid="ressource-lead"
              className="mt-8 lg:mt-10 text-lg lg:text-xl text-[#5A5E64] font-normal leading-relaxed text-pretty"
            >
              {article.resume}
            </p>

            <div className="mt-10 mb-12 flex items-center gap-5 flex-wrap">
              {article.updated ? (
                <span
                  data-testid="ressource-updated"
                  className="font-mono text-[11px] tracking-[0.14em] text-[#7C8086]"
                >
                  Mise à jour : {article.updated}
                </span>
              ) : null}
              <span aria-hidden="true" className="h-[2px] w-10 bg-[#E94B3C]" />
            </div>

            {/* Photo de couverture éditoriale */}
            <figure
              data-testid="ressource-cover"
              className="mb-14 -mx-0 overflow-hidden rounded-lg border border-[#E2DEDA]"
            >
              <img
                src={`/images/comprendre/${article.slug}.jpg`}
                alt=""
                className="w-full aspect-[16/7] object-cover"
              />
            </figure>

            <div data-testid="ressource-body" className="max-w-none">
              {article.body.map((block, i) => (
                <Block key={`${block.type}-${String(block.text || block.title || "").slice(0, 40)}-${i}`} block={block} />
              ))}
            </div>
          </div>
        </article>

        {/* Related — discreet library-style cross-links */}
        <section className="bg-[#FAF9F7]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
            <div className="max-w-3xl mx-auto">
              <div className="h-px bg-[#E2DEDA] mb-12 lg:mb-16" />
              <p className="label-eyebrow text-[#666A70] mb-8">
                Continuer la lecture
              </p>
              <ul
                data-testid="ressource-related"
                className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4"
              >
                {related.map((r) => (
                  <li
                    key={r.slug}
                    className="bg-white rounded-md border border-[#E2DEDA] overflow-hidden transition-shadow duration-500 hover:shadow-[0_4px_24px_-12px_rgba(10,17,40,0.18)]"
                  >
                    <Link
                      to={`/comprendre/${r.slug}`}
                      data-testid={`ressource-related-${r.slug}`}
                      className="group block h-full px-6 py-7 focus:outline-none"
                    >
                      <p className="label-eyebrow text-[#666A70] mb-4">
                        {r.categorie}
                      </p>
                      <h3 className="font-serif font-normal text-[#181A1D] text-lg leading-snug tracking-tight mb-4 text-balance">
                        {r.titre}
                      </h3>
                      <span className="text-[11px] tracking-[0.18em] uppercase font-medium text-[#181A1D] inline-flex items-center gap-2 transition-opacity group-hover:opacity-70">
                        Lire
                        <ArrowUpRight
                          size={12}
                          strokeWidth={1.5}
                          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
